import { z } from './utils/zod.js';
import { dataResponse, deleteResponse, errorCodeResponse, errorResponse, listResponse, successResponse, } from './utils/response-templates.js';
import { ErrorCodes } from './error-codes.js';
import { auditTrailRecordSchema } from './audit-trail.js';
import { stampSizeSchema } from './stamp-size.js';
export const signingDocumentSchema = z.object({
    id: z.string().uuid(),
    filename: z.string().min(4).max(150),
    byteSize: z.string(),
    pageCount: z.number().positive(),
});
export const signerComponentSchema = z.object({
    id: z.string().uuid(),
    documentId: z.string().uuid(),
    // TODO: Add a nullable signedAt to know when this placeholder is actually signed by signer
    type: z.string().max(50), // TODO: Define 'hidden' | 'full_signature' | 'short_signature'
    properties: z.object({
        page: z.number(),
        x: z.number(),
        y: z.number(),
    }),
});
export var SignerRole;
(function (SignerRole) {
    SignerRole["VIEWER"] = "viewer";
    SignerRole["SIGNER"] = "signer";
})(SignerRole || (SignerRole = {}));
export const signerRoleSchema = z.nativeEnum(SignerRole);
export const signerKindSchema = z.enum(['person', 'company']);
export const signerSchema = z.object({
    id: z.string().uuid(),
    // For kind 'company' this is the company name; representatives carry their own person name.
    name: z.string().trim().min(1).max(150),
    email: z.string().commonEmail(),
    phone: z.string().phoneNumber('Invalid phone number', { emptyStringAllowed: true }).optional(),
    role: signerRoleSchema,
    components: z.array(signerComponentSchema).nullable(),
    color: z.string().max(50),
    needsLogin: z.boolean().optional(),
    contactId: z.string().uuid().nullable().optional(), // TODO: remove .optional() once FE always sends this property
    kind: signerKindSchema.optional(), // absent = 'person'
    representativeOf: z.string().uuid().optional(), // signer id of the company this rep signs for
});
export const formattedPhoneSchema = z.object({
    number: z.string(),
    countryCode: z.string().optional(),
    formattedNumber: z.string(),
});
export const signerOutputSchema = signerSchema.omit({ email: true, phone: true }).extend({
    email: z.string().emailOrMasked(),
    phone: formattedPhoneSchema.optional(),
    canSign: z.boolean(),
});
const validateUniqueSignerIds = (signers) => {
    const signerIds = new Set();
    for (const signer of signers) {
        if (signerIds.has(signer.id))
            return false;
        signerIds.add(signer.id);
    }
    return true;
};
const validateUniqueComponentIds = (signers) => {
    const componentIds = new Set();
    for (const signer of signers) {
        if (!signer.components)
            continue;
        for (const component of signer.components) {
            if (componentIds.has(component.id))
                return false;
            componentIds.add(component.id);
        }
    }
    return true;
};
export const signersSchema = z
    .array(signerSchema)
    .refine((signers) => validateUniqueSignerIds(signers) && validateUniqueComponentIds(signers), {
    message: 'Signer IDs and Component IDs must be unique across all signers in a signing.',
});
export const signersOutputSchema = z
    .array(signerOutputSchema)
    .refine((signers) => validateUniqueSignerIds(signers) && validateUniqueComponentIds(signers), {
    message: 'Signer IDs and Component IDs must be unique across all signers in a signing.',
});
export const signingInitiationSchema = z.object({
    subject: z.string().min(1).max(150).openapi({ example: 'Please sign the document' }),
    message: z.string().max(1500).openapi({ example: 'Please sign the document' }),
});
export const signingInvitationsSchema = z.object({
    signers: signingInitiationSchema.nullable(),
    viewers: signingInitiationSchema.nullable(),
});
export const signatureTypeSchema = z.enum([
    //  'SIMPLE',
    'ADVANCED',
    'QUALIFIED',
    //  'NOTARIZED'
]);
export const signingCollectionReviewerSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(150),
    email: z.string().commonEmail(),
    contactId: z.string().uuid().nullable(),
});
export const supportedLanguageSchema = z.enum(['en', 'de', 'zh-Hant-TW', 'it', 'fr', 'es', 'cs', 'sk']);
export const createSigningRequestSchema = z.object({
    name: z.string().max(150),
    signatureType: z.string().max(50).nullable(),
    strictOrder: z.boolean(),
    skipDocumentEmail: z.boolean().optional(),
    expirationDate: z.string().datetime().nullable(),
    signers: signersSchema,
    invitation: signingInvitationsSchema,
    enhancedSecurity: z.boolean().optional(),
    shared: z.boolean().default(false),
    reviewers: z.array(signingCollectionReviewerSchema).optional(),
    logReviewInAuditTrail: z.boolean().optional(),
    language: z.string().optional(),
    documentOrder: z.array(z.string().uuid()).optional(),
    stampSize: stampSizeSchema.optional(),
});
export const signingDtoSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    state: z.string(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    canceledAt: z.string().datetime().nullable(),
    expiredAt: z.string().datetime().nullable(),
    lockedForSigningAt: z.string().datetime().nullable(),
    name: z.string().max(150),
    signatureType: z.string().max(50).nullable(),
    strictOrder: z.boolean(),
    expirationDate: z.string().datetime().nullable(),
    documents: z.array(signingDocumentSchema),
    signers: signersSchema.nullable(),
    invitation: signingInvitationsSchema.nullable(),
    enhancedSecurity: z.boolean().optional(),
    shared: z.boolean(),
    skipDocumentEmail: z.boolean(),
    reviewers: z.array(signingCollectionReviewerSchema).optional(),
    language: z.string(),
    stampSize: stampSizeSchema,
});
export const signingDtoOutputSchema = signingDtoSchema.omit({ signers: true }).extend({
    signers: signersOutputSchema.nullable(),
});
export const createSigningResponseSchema = successResponse().extend({
    data: signingDtoOutputSchema,
});
export const updateSigningRequestSchema = createSigningRequestSchema;
export const updateSigningResponseSchema = successResponse().extend({
    data: signingDtoOutputSchema,
});
export const signingNotFoundResponseSchema = errorResponse('Signing not found');
export const deleteSigningResponseSchema = deleteResponse();
export const signingActionNotAllowedResponseSchema = errorResponse('Signing action is not allowed at the moment').extend({
    reason: z.string().optional(),
    code: z.string(),
});
export const startedBySchema = z.object({
    email: z.string().email().optional(),
    name: z.string().optional(),
});
export const listSigningsDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string().max(150),
    signatureType: z.string().max(50).nullable(),
    expirationDate: z.string().datetime().nullable(),
    strictOrder: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    state: z.string(), //z.enum(['DRAFT', 'IN_REVIEW', 'STARTING', 'SIGNING', 'CANCELLED', 'EXPIRED', 'COMPLETED']),
    signers: signersOutputSchema,
    startable: z.boolean(),
    archivedAt: z.string().datetime().nullable(),
    isArchived: z.boolean(),
    shared: z.boolean(),
});
export const getSigningSchema = signingDtoOutputSchema.extend({
    startable: z.boolean(),
    archivedAt: z.string().datetime().nullable(),
    isArchived: z.boolean(),
    skipDocumentEmail: z.boolean(),
    logReviewInAuditTrail: z.boolean(),
    startedBy: startedBySchema.nullable(),
    companyRepresentativesCap: z.number().optional(),
});
export const listSigningsResponseSchema = listResponse(listSigningsDtoSchema);
export const awaitingSignatureDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    sentAt: z.string().datetime(),
    expirationDate: z.string().datetime().nullable(),
    signerId: z.string().uuid(),
    createdBy: z.object({
        id: z.string().uuid(),
        name: z.string(),
    }),
});
export const awaitingSignaturesResponseSchema = listResponse(awaitingSignatureDtoSchema);
export const getSigningResponseSchema = dataResponse(getSigningSchema);
export const inviteSignerRequestSchema = z.object({
    signingId: z.string().uuid(),
});
export const inviteSignerResponseSchema = successResponse();
export const signingInvalidResponseSchema = errorResponse('Invalid signing');
export const documentComponentSchema = z.object({
    id: z.string(),
    pageCount: z.number(),
});
export const cancelSigningResponseSchema = successResponse();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const auditTrail = z.object({
    actionType: z.string(),
    sourceUser: z.string().optional(),
    createdAt: z.string(),
    id: z.string().uuid(),
    oldValue: z.string().nullable(),
    value: z.string().nullable(),
    field: z.string().nullable(),
});
export const auditTrailRecordDtoSchema = auditTrailRecordSchema
    .omit({
    requestId: true,
    createdAt: true,
})
    .extend({
    date: z.string().datetime().openapi({ example: '2023-10-05T14:48:00.000Z' }),
});
export const getSigningAuditTrailResponseSchema = listResponse(auditTrailRecordDtoSchema);
export const signingSignMethod = z.object({
    countries: z.array(z.string()),
    method: z.string(),
});
export const signingSignStartResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        pending: z.literal(true).openapi({ example: true }),
        verificationMethod: z.enum(['sms', 'email']).optional(),
    }),
});
export const signingSignResponseSchema = z.object({
    success: z.boolean(),
    data: z.discriminatedUnion('pending', [
        z.object({
            pending: z.literal(true).openapi({ example: true }),
        }),
        z.object({
            pending: z.literal(false).openapi({ example: false }),
            signUrl: z.string(),
            successUrl: z.string(),
            errorUrl: z.string(),
            enforceProvider: z.string().nullable(),
            // When the enforced A-Trust flow originates from a WebID verification,
            // show the "use your WebID mobile number + password" hint. False for ID
            // Austria-KYC'd signers, where that hint is wrong.
            enforceWebidHint: z.boolean(),
            // True once a guest signer has passed the WebID identity gate. The sign
            // flow polls this to resume the A-Trust flow after WebID returns.
            webidVerified: z.boolean(),
            provider: z.object({
                clientId: z.string(),
                docId: z.string(),
                availableMethods: z.array(signingSignMethod),
            }),
            country: z.string(), // organization country
            language: z.string(),
            userCountry: z.string(), // country of current user
        }),
    ]),
});
export const signingSignCompleteResponseSchema = z.object({
    success: z.boolean(),
});
export const signerResponseDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().emailOrMasked(),
    phone: z
        .object({
        number: z.string(),
        countryCode: z.string().optional(),
        formattedNumber: z.string(),
    })
        .optional(),
    role: signerRoleSchema,
    // components: z.array(signerComponentSchema).nullable(),
    color: z.string(),
    state: z.string(), //z.enum(['PENDING', 'SIGNING', 'SIGNED', 'EXPIRED']),
    signedAt: z.string().datetime().nullable(),
    needsLogin: z.boolean().optional(),
    kind: signerKindSchema.optional(),
    representativeOf: z.string().uuid().optional(),
});
export const getSignersResponseSchema = dataResponse(z.array(signerResponseDtoSchema));
export const signComponentDataSchema = z.object({
    id: z.string().uuid(),
    date: z.string().datetime(),
});
export const signRequestSchema = z.object({
    components: z.array(signComponentDataSchema),
});
export const getSigningSignableSchema = z.object({
    signable: z.boolean(),
    signingBy: z.string().nullable(),
});
export const abortSignResponseSchema = successResponse();
export const signerNotFoundResponseSchema = errorResponse('Signer not found');
export const signAssignRequestSchema = z.object({
    signToken: z.string(),
});
export const signAssignResponseSchema = successResponse();
export const confirmAesPhoneRequestSchema = z.object({
    code: z.string().length(6),
});
export const confirmAesPhoneResponseSchema = successResponse().extend({
    token: z.string(),
});
export const aesPhoneVerificationLimitErrorResponseSchema = errorCodeResponse(ErrorCodes.EVerificationLimitReached, 'Verification limit reached, please try again later');
export const aesPhoneVerificationThrottleErrorResponseSchema = errorCodeResponse(ErrorCodes.EVerificationThrottleReached, 'Too many requests, please try again later');
export const verifyAccessSigningRequestBodySchema = z.object({
    password: z.string().optional(),
    smsCode: z.string().optional(),
});
export const verifyAccessResponseSchema = successResponse().extend({
    data: z.object({
        verificationToken: z.string(),
    }),
});
export const verifyAccessUnprocessableEntityResponseSchema = errorResponse('Invalid verification information');
export const sendSignerReminderResponseSchema = successResponse();
export const signerCannotReceiveReminderResponseSchema = errorCodeResponse(ErrorCodes.ESignerCannotReceiveReminder, 'Signer cannot receive a reminder in their current state');
export const reassignSignerRequestSchema = z.object({
    name: z.string().trim().min(1).max(150),
    email: z.string().commonEmail(),
    phone: z.string().phoneNumber('Invalid phone number', { emptyStringAllowed: true }).optional(),
});
export const reassignSignerResponseSchema = successResponse();
export const nameRepresentativesRequestSchema = z.object({
    representatives: z
        .array(z.object({
        name: z.string().trim().min(1).max(150),
        email: z.string().commonEmail(),
        phone: z.string().phoneNumber('Invalid phone number', { emptyStringAllowed: true }).optional(),
    }))
        .min(1),
});
export const nameRepresentativesResponseSchema = dataResponse(z.object({
    representatives: z.array(z.object({ signerId: z.string().uuid(), name: z.string(), email: z.string() })),
}));
export const signerCannotBeReassignedResponseSchema = errorCodeResponse(ErrorCodes.ESignerCannotBeReassigned, 'Signer cannot be reassigned in their current state');
